import { FiBell } from "react-icons/fi";

export const MAX_DAYS_BEFORE = 60;
export const emptyReminders = () => ({ scheme_end: null, redeem_end: null });

const TIMES = [];
for (let h = 0; h < 24; h += 1) for (const m of [0, 15, 30, 45]) TIMES.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);

const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

// "YYYY-MM-DD" minus N days -> "D MMM YYYY"
const startDay = (endDate, daysBefore) => {
  if (!endDate || daysBefore === "" || daysBefore === null || daysBefore === undefined || Number.isNaN(Number(daysBefore))) return "";
  const d = new Date(`${String(endDate).substring(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() - Number(daysBefore));
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/** Payload form: only enabled sections with a whole-number day count. */
export const normalizeReminders = (value) => {
  const out = {};
  ["scheme_end", "redeem_end"].forEach((k) => {
    const r = value?.[k];
    if (r && r.days_before !== "" && r.days_before !== null && r.days_before !== undefined && r.time) {
      out[k] = { days_before: Number(r.days_before), time: r.time };
    }
  });
  return out;
};

/** First validation problem, or "" when fine. */
export const validateReminders = (value, { endDate, redeemEnd }) => {
  const sections = [
    ["scheme_end", "Scheme-ending reminder", endDate, "scheme end"],
    ["redeem_end", "Redemption-closing reminder", redeemEnd, "redeem end"],
  ];
  for (const [key, label, date, dateLabel] of sections) {
    const r = value?.[key];
    if (!r) continue;
    if (!date) return `${label}: set the ${dateLabel} date first`;
    const d = r.days_before;
    if (d === "" || d === null || d === undefined || !Number.isInteger(Number(d)) || Number(d) < 0 || Number(d) > MAX_DAYS_BEFORE) {
      return `${label}: days before must be a whole number from 0 to ${MAX_DAYS_BEFORE}`;
    }
    if (!r.time) return `${label}: choose a time`;
  }
  return "";
};

export const formatReminders = (n) => {
  const parts = [];
  [["scheme_end", "Scheme end"], ["redeem_end", "Redeem end"]].forEach(([k, label]) => {
    const r = n?.[k];
    if (r) parts.push(`${label}: daily from ${r.days_before} day(s) before, at ${r.time}`);
  });
  return parts.length ? parts.join(" · ") : "—";
};

const Section = ({ title, hint, value, endDate, endLabel, onChange }) => {
  const disabled = !endDate;
  const enabled = !!value;
  const start = enabled ? startDay(endDate, value.days_before) : "";
  return (
    <div className={`rounded-xl border border-gray-200 p-4 ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
          <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700 shrink-0">
          <input
            type="checkbox"
            disabled={disabled}
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? { days_before: "", time: "10:30" } : null)}
          />
          Enable
        </label>
      </div>

      {disabled && <p className="mt-3 text-xs text-amber-700">Set the {endLabel} date to enable this reminder.</p>}

      {enabled && (
        <div className="mt-3 flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-600">Start</span>
          <input
            type="number"
            min={0}
            max={MAX_DAYS_BEFORE}
            step={1}
            value={value.days_before}
            onChange={(e) => onChange({ ...value, days_before: e.target.value })}
            placeholder="X"
            className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <span className="text-xs text-gray-600">day(s) before, daily at</span>
          <select
            value={value.time}
            onChange={(e) => onChange({ ...value, time: e.target.value })}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {TIMES.map((t) => <option key={t} value={t}>{fmt12(t)}</option>)}
          </select>
          <span className="text-[11px] text-gray-400">IST</span>
        </div>
      )}
      {enabled && start && (
        <p className="mt-2 text-xs text-blue-700">Daily from {start} until {String(endDate).substring(0, 10)}.</p>
      )}
    </div>
  );
};

/**
 * Daily push reminders from X days before the end date until it arrives.
 * value: { scheme_end: null | {days_before, time}, redeem_end: null | {...} } (days_before may be "" while typing)
 */
const SchemeReminders = ({ value, onChange, endDate, redeemEnd }) => (
  <div className="mt-6">
    <div className="flex items-center gap-2 mb-1">
      <FiBell className="text-gray-500" />
      <h3 className="text-sm font-semibold text-gray-800">Push reminders</h3>
    </div>
    <p className="text-xs text-gray-500 mb-3">
      Notify app users every day, starting X days before the date, at the chosen time. Scheme-ending reminders go to all eligible users;
      redemption-closing reminders go only to users with points left to redeem. You can also send either one manually from the Scheme Details page.
    </p>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Section
        title="Scheme ending"
        hint="Counted back from the scheme end date."
        value={value.scheme_end}
        endDate={endDate}
        endLabel="scheme end"
        onChange={(v) => onChange({ ...value, scheme_end: v })}
      />
      <Section
        title="Redemption closing"
        hint="Counted back from the redeem end date."
        value={value.redeem_end}
        endDate={redeemEnd}
        endLabel="redeem end"
        onChange={(v) => onChange({ ...value, redeem_end: v })}
      />
    </div>
  </div>
);

export default SchemeReminders;
